// Roda a Trend Structure no navegador (o mesmo motor da Edge Function) para desenhar a estrutura que o
// próprio motor calcula: um HH, um HL, um LH e um LL por tendência, com estágio, e os topos/fundos
// internos em cinza. Aqui não tem regra própria: tudo vem do motor. Não grava nada no banco.
import { calculateTrendStructureFromCandles, caStructurePoints, type TrendCandle } from '@trend-engine/trend-structure.mjs'
import type { ChartCandle } from './trendShadowService'

export type PivotStage = 'confirmado' | 'candidato' | 'formacao' | 'interno'

export type EnginePoint = {
  label: string // HH, HL, LH, LL, ou "topo"/"fundo" para os internos
  price: number
  at: string
  stage: PivotStage
  side: 'top' | 'bottom'
  role: 'origem' | 'extremo' | 'defendido' | 'contra' | 'interno'
  via?: 'fechamento' | 'pavio' | null
  defended?: boolean
}

export type StructureMark = {
  type: string
  price: number
  at: string
  stage: PivotStage
  role: EnginePoint['role']
  via?: 'fechamento' | 'pavio' | null
  defended?: boolean
}

export type EngineStructure = {
  rule: string
  trend: 'bullish' | 'bearish'
  hh: StructureMark | null
  hl: StructureMark | null
  lh: StructureMark | null
  ll: StructureMark | null
}

export type EngineView = {
  snapshot: Record<string, any>
  structure: EngineStructure | null
  points: EnginePoint[]
}

const TF_MS: Record<string, number> = {
  '5': 5 * 60e3, '15': 15 * 60e3, '30': 30 * 60e3, '60': 60 * 60e3,
  '240': 240 * 60e3, '720': 720 * 60e3, D: 1440 * 60e3, W: 7 * 1440 * 60e3,
}

function candleEnd(tf: string, startMs: number) {
  if (tf === 'M') {
    const d = new Date(startMs)
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1)
  }
  return startMs + TF_MS[tf]
}

export function toEngineCandles(tf: string, candles: ChartCandle[]): TrendCandle[] {
  const now = Date.now()
  return candles
    .map((c) => {
      const s = Date.parse(c.open_time)
      return { startAt: new Date(s).toISOString(), endAt: new Date(candleEnd(tf, s)).toISOString(), open: c.open, high: c.high, low: c.low, close: c.close }
    })
    .filter((c) => Date.parse(c.endAt) <= now) // só candles fechados, como na sombra
}

export function computeEngineView(tf: string, candles: ChartCandle[]): EngineView | null {
  const engineCandles = toEngineCandles(tf, candles)
  if (engineCandles.length < 5) return null
  const { snapshot } = calculateTrendStructureFromCandles(engineCandles)
  const points = (caStructurePoints(snapshot) as Record<string, any>[]).map((p) => ({
    label: String(p.label),
    price: Number(p.price),
    at: String(p.at),
    stage: p.stage as PivotStage,
    side: p.side as 'top' | 'bottom',
    role: p.role as EnginePoint['role'],
    via: p.via ?? null,
    defended: Boolean(p.defended),
  }))
  return { snapshot, structure: (snapshot?.structure ?? null) as EngineStructure | null, points }
}
